import { Box, Typography } from "@mui/material";
import React, { FC, useEffect, useRef, useState } from "react";

import { chapterAt, formatClock } from "../helpUtils";
import { HelpChapter } from "../types";

const PREVIEW_WIDTH = 160;
const PREVIEW_HEIGHT = 90;
const PREVIEW_THROTTLE_MS = 100;
const BAR_HEIGHT = 6;

interface SeekBarProps {
  /** The video's signed URL; undefined until the play links arrive. */
  src?: string;
  duration: number;
  time: number;
  chapters: HelpChapter[];
  onSeek: (seconds: number) => void;
}

/** Progress bar with timeline ticks; hovering shows the time, entry title and a frame preview. */
const SeekBar: FC<SeekBarProps> = ({ src, duration, time, chapters, onSeek }) => {
  const barRef = useRef<HTMLDivElement>(null);
  const previewVideo = useRef<HTMLVideoElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const lastSeek = useRef(0);
  const [hover, setHover] = useState<{ x: number; seconds: number } | null>(null);
  const [frameReady, setFrameReady] = useState(false);

  useEffect(() => setFrameReady(false), [src]);

  const secondsAt = (clientX: number): { x: number; seconds: number } => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, seconds: 0 };
    const x = Math.min(rect.width, Math.max(0, clientX - rect.left));
    return { x, seconds: (x / rect.width) * duration };
  };

  const onMove = (event: React.MouseEvent) => {
    const next = secondsAt(event.clientX);
    setHover(next);
    const now = Date.now();
    if (previewVideo.current && now - lastSeek.current >= PREVIEW_THROTTLE_MS) {
      lastSeek.current = now;
      previewVideo.current.currentTime = next.seconds;
    }
  };

  const drawFrame = () => {
    const context = canvas.current?.getContext("2d");
    if (!context || !previewVideo.current) return;
    context.drawImage(previewVideo.current, 0, 0, PREVIEW_WIDTH, PREVIEW_HEIGHT);
    setFrameReady(true);
  };

  const title = hover ? chapterAt(chapters, hover.seconds)?.title : undefined;
  const percent = (seconds: number) => (duration ? `${Math.min(100, (seconds / duration) * 100)}%` : "0%");

  return (
    <Box
      ref={barRef}
      role="slider"
      aria-label="Seek"
      aria-valuemin={0}
      aria-valuemax={Math.floor(duration)}
      aria-valuenow={Math.floor(time)}
      onMouseMove={onMove}
      onMouseLeave={() => setHover(null)}
      onClick={(event) => duration && onSeek(secondsAt(event.clientX).seconds)}
      sx={{ position: "relative", py: 1, cursor: "pointer" }}
    >
      <Box sx={{ position: "relative", height: BAR_HEIGHT, borderRadius: 1, bgcolor: "divider" }}>
        <Box sx={{ position: "absolute", inset: 0, width: percent(time), bgcolor: "primary.main", borderRadius: 1 }} />
        {chapters.slice(1).map((chapter) => (
          <Box
            key={chapter.start}
            sx={{ position: "absolute", top: 0, bottom: 0, width: 2, left: percent(chapter.start), bgcolor: "background.paper" }}
          />
        ))}
      </Box>
      <Box
        component="video"
        ref={previewVideo}
        src={src}
        muted
        // Metadata only: hovering fetches just the frames it shows, not a second copy of the video.
        preload="metadata"
        crossOrigin="anonymous"
        onSeeked={drawFrame}
        sx={{ display: "none" }}
      />
      {hover && (
        <Box
          sx={{
            position: "absolute",
            bottom: "100%",
            left: hover.x,
            transform: "translateX(-50%)",
            mb: 0.5,
            p: 0.5,
            width: frameReady ? PREVIEW_WIDTH + 8 : "auto",
            borderRadius: 1,
            bgcolor: "background.paper",
            color: "text.primary",
            boxShadow: 3,
            pointerEvents: "none",
            textAlign: "center",
            zIndex: 1,
          }}
        >
          <Box
            component="canvas"
            ref={canvas}
            width={PREVIEW_WIDTH}
            height={PREVIEW_HEIGHT}
            sx={{ display: frameReady ? "block" : "none", width: PREVIEW_WIDTH, height: PREVIEW_HEIGHT, borderRadius: 0.5, mb: 0.5 }}
          />
          <Typography variant="caption" noWrap display="block" textAlign="center">
            {formatClock(hover.seconds)}
            {title && ` · ${title}`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SeekBar;
